import {AttrsSchema, Component, ConfigOptions} from 'panel';
import {VNode} from 'snabbdom';
import debounce from 'lodash/debounce';
import ResizeObserver from 'resize-observer-polyfill';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {createElem, propagateEvent, rafRetry} from 'common/util/dom';
import {panel2react} from 'common/util/react';
import mpFonts from 'common/stylesheets/mixins/fonts.json';
import {MeasureUtil} from 'common/util';

import {
  AutocompleteType,
  InputChangeEvent,
  InputSize,
  InputState,
  InputTheme,
  InputType,
  Necessary,
  TextAlign,
  ValidationStatus,
} from './types';
import {template} from './template';
import css from './index.inline.styl';
import './label';

export type InputAttrs = {
  autocomplete?: AutocompleteType;
  'action-text'?: string;
  ariaLabel?: string;
  'auto-focus'?: boolean;
  'multiline-prefix'?: boolean;
  'not-focusable'?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  'field-id'?: string;
  'field-name'?: string;
  icon?: string;
  label?: string;
  'show-clear'?: boolean;
  'always-show-clear'?: boolean;
  placeholder?: string;
  'pre-text'?: string;
  type?: string;
  size?: string;
  necessary?: string;
  'validation-status'?: string;
  'error-message'?: string;
  maxlength?: number;
  'help-text'?: string;
  'extra-info'?: string;
  min?: string; // use "string" to avoid default value for number type
  max?: string;
  'min-input-width'?: number;
  'select-on-focus'?: boolean;
  'text-align'?: TextAlign;
  'hide-focus-styling'?: boolean;
  theme?: string;
  'show-numeric-input-controls'?: boolean;
};

type InputParams = {
  /** renders content before the input element and after any icons */
  prefixRenderer?: () => VNode | VNode[];
  /** renders content after the input element */
  suffixRenderer?: () => VNode | VNode[];
};

interface InputJsxProps {
  attrs?: InputAttrs;
  props?: {
    value?: string;
  };
  params?: InputParams;
  on?: {
    input?: (ev) => void;
    change?: (ev) => void;
    clear?: (ev) => void;
    blur?: (ev) => void;
    keydown?: (ev) => void;
  };
}

export class InputComponent extends Component<
  InputState,
  InputJsxProps['attrs'],
  unknown,
  unknown,
  unknown,
  InputParams
> {
  static useShadowDom = true;

  static get attrsSchema(): AttrsSchema<InputJsxProps['attrs']> {
    return {
      ...super.attrsSchema,
      autocomplete: {
        type: `string`,
        enum: Object.values(AutocompleteType),
        default: AutocompleteType.Off,
      },
      ariaLabel: {type: `string`},
      'auto-focus': {type: `boolean`, description: `If set, automatically focuses on the input field`},
      'not-focusable': `boolean`,
      'multiline-prefix': `boolean`,
      'hide-focus-styling': {
        type: `boolean`,
        description: `Hide focus styling, typically because a parent is styling the focus state`,
      },
      disabled: {type: `boolean`, description: `Whether input is disabled`},
      readonly: {type: `boolean`, description: `Whether input is readonly`},
      'field-id': {type: `string`, description: `Id of input field, maps to field label for accessibility`},
      'field-name': {type: `string`, description: `Name of the input field, maps to field label`},
      icon: {type: `string`, default: ``, description: `Left icon that goes inline with the input`},
      label: {type: `string`, default: ``, description: `Label on top that describes the input field`},
      'show-clear': {type: `boolean`, description: `Show clear input button when there's no input`},
      'always-show-clear': {type: `boolean`, description: `Show clear input button regardless if there's input or not`},
      placeholder: {type: `string`, description: `Placeholder text for input`},
      'pre-text': {type: `string`, description: `Mono-width text at the beginning of the input`},
      'action-text': {
        type: `string`,
        description: `Feedback for user to perform action when the input is focused. e.g. "Press enter to search"`,
      },
      type: {type: `string`, enum: Object.values(InputType), default: InputType.Text},
      size: {type: `string`, enum: Object.values(InputSize), default: InputSize.Large},
      necessary: {type: `string`, enum: Object.values(Necessary), default: Necessary.Default},
      'validation-status': {type: `string`, enum: Object.values(ValidationStatus), default: ValidationStatus.Default},
      'error-message': {type: `string`, description: `Error message to show when invalid validation status`},
      maxlength: {type: `number`, description: `Maximum number of characters allowed`, default: Necessary.Optional},
      'help-text': {
        type: `string`,
        description: `If set, an "info" icon will be shown next to the label. This value will be shown as a tooltip on hover`,
      },
      'extra-info': {
        type: `string`,
        description: `If set, we will show this string next to the label. This is useful to give the user additional context such as which SDK verion a field is supported in`,
      },
      min: `string`, // use "string" to avoid default value for number type
      max: `string`,
      'min-input-width': {
        type: `number`,
        description:
          `Minimum width of the internal input element in pixels. This should be set when there's` +
          ` a prefixRenderer (which takes up horizontal space) to prevent the input element` +
          ` from shrinking below some minimum width.`,
      },
      'select-on-focus': {
        type: `boolean`,
        description: `Used for fields where user will most likely replace the whole input value instead of editing portions of the input`,
      },
      'text-align': {
        type: `string`,
        enum: Object.values(TextAlign),
        default: TextAlign.Start,
      },
      theme: {
        type: `string`,
        enum: Object.values(InputTheme),
        default: InputTheme.Default,
      },
      'show-numeric-input-controls': {
        type: `boolean`,
      },
    };
  }

  searchInput: HTMLInputElement;
  inputWrapperEl: HTMLElement;
  inputPrefixEl: HTMLElement;
  prefixResizeObserver: ResizeObserver;

  debouncedChange = debounce(() => {
    this.dispatchEvent(new CustomEvent(`change`, {detail: {value: this.state.value}} as InputChangeEvent));
  }, 1000);

  get config(): ConfigOptions<InputState, unknown, unknown, InputParams> {
    // @ts-ignore - useShadowDom is a static var
    const useShadowDom = this.constructor.useShadowDom;
    return {
      css: useShadowDom ? css : null,
      template,
      useShadowDom,
      defaultState: {
        value: ``,
        textWidth: 0,
      },
      params: {
        prefixRenderer: Function,
        suffixRenderer: Function,
      },
      helpers: {
        handleInput: (ev) => {
          const value = ev.target.value;
          this.changeValue(value);
          propagateEvent(this, ev);
        },
        handleChange: (ev) => {
          ev.stopPropagation();
          const value = ev.target.value;
          this.dispatchEvent(new CustomEvent(`change`, {detail: {value}} as InputChangeEvent));
          // Note: do NOT blur the input here; if a widget needs to blur-on-change, it should be done in the widget itself as a special case to avoid breaking browser-standard behavior by default
        },
        handleBlur: (ev) => {
          ev.stopPropagation();
          this.dispatchEvent(new CustomEvent(`blur`, ev));
        },
        handleClearInput: (ev) => {
          ev.stopPropagation();
          this.changeValue(``);
          this.dispatchEvent(new CustomEvent(`clear`, ev));
          // without this, we focus on mp-input instead of just the input field. focusing on
          // mp-input prevents us from typing our second query, while focusing on the searchInput
          // allows to type something else.
          this.focus();
        },
        handleFocus: (): void => {
          if (this.attr(`select-on-focus`)) {
            this.select();
          }
        },
      },
    };
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.attr(`auto-focus`)) {
      this.focus();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.prefixResizeObserver?.disconnect();
  }

  attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);
    //Focus element if auto-focus attribute is added
    if (attr === `auto-focus` && oldVal === null) {
      this.focus();
    } else if (attr === `placeholder`) {
      this.updateInputWidth();
    }
  }

  changeValue(value: string) {
    this.updateInputWidth(value);
    this.update({value});
  }

  get selectionStart() {
    return this.searchInput?.selectionStart;
  }

  scrollToStart(): void {
    if (this.inputWrapperEl) {
      this.inputWrapperEl.scrollLeft = 0;
    }
  }

  get value() {
    return this.state.value;
  }

  set value(value: string) {
    this.updateInputWidth(value);
    this.update({value});
  }

  /**
   * For auto resizing inputs, makes sure that the scrolling area is
   * shifted to the right as the user types or content grows.
   *
   * This behavior mimics the native input tag with overflowing content.
   */
  updateInputWidth(value: string = this.state.value) {
    if (!this.attr(`multiline-prefix`)) {
      return;
    }

    value ||= this.attr(`placeholder`) ?? null;
    if (value) {
      const currentTextWidth = this.state.textWidth ?? 0;

      const fontSizeMap = {
        [InputSize.Large]: mpFonts.fontSizeLarge,
        [InputSize.Medium]: mpFonts.fontSizeMedium,
        [InputSize.Small]: mpFonts.fontSizeSmall,
      };

      const textWidth =
        MeasureUtil.measureTextWidth(value, `400 ${fontSizeMap[this.attr(`size`)]} ${mpFonts.fontFamilyPrimary}`) + 16;

      this.update({textWidth});
      if ((this.searchInput && textWidth > currentTextWidth) ?? 0) {
        rafRetry(() => {
          this.searchInput.parentElement.scrollLeft = this.searchInput.parentElement.scrollWidth;
          return true;
        });
      }
    } else {
      this.update({textWidth: null});
    }
  }

  async focus() {
    await rafRetry(() => {
      this.searchInput?.focus();
      this.searchInput.parentElement.scrollLeft = this.searchInput.parentElement.scrollWidth;
      if (this.attr(`select-on-focus`)) {
        this.select();
      }
      return Boolean(this.searchInput);
    });
  }

  blur() {
    this.searchInput?.blur();
    return super.blur();
  }

  select() {
    return this.searchInput?.select();
  }

  handleInputWrapperInsert(elm: HTMLElement): void {
    this.inputWrapperEl = elm;
  }

  // rename prefix wrapper
  handleInputPrefixInsert(elm: HTMLElement): void {
    this.inputPrefixEl = elm;
    // don't think this is needed because we should just enter this codepath once? but just in case
    this.prefixResizeObserver?.disconnect();
    this.prefixResizeObserver = new ResizeObserver(() => {
      const visibleInputWidth =
        this.inputWrapperEl.getBoundingClientRect().right - this.searchInput.getBoundingClientRect().left;
      // add a buffer to visible input width to account for off by one type issues
      const bufferPixels = 5;
      if (visibleInputWidth + bufferPixels < this.attr(`min-input-width`)) {
        this.inputWrapperEl.scrollLeft += this.attr(`min-input-width`) - visibleInputWidth;
      }
    });
    this.prefixResizeObserver.observe(this.inputPrefixEl);
  }

  handleSearchInputInsert(elm: HTMLInputElement) {
    this.searchInput = elm;
  }
}

class ShadowlessInput extends InputComponent {
  static useShadowDom = false;

  connectedCallback() {
    super.connectedCallback();

    // add mp-input styles as global styles if not already added
    if (!document.head.querySelector(`style#mp-input-css`)) {
      document.head.appendChild(createElem(`style`, {type: `text/css`}, css.toString()));
    }
  }
}

export const ShadowlessInputEl = defineAndMakeJsxFactory<InputJsxProps>(`mp-shadowless-input`, ShadowlessInput);
export const ShadowlessInputReact = panel2react<InputJsxProps>(`mp-shadowless-input`, ShadowlessInput);
export const InputEl = defineAndMakeJsxFactory<InputJsxProps>(`mp-input`, InputComponent);
export const InputReact = panel2react<InputJsxProps>(`mp-input`, InputComponent);
